import { useState, useRef, useEffect, useCallback } from "react";
import { uploadInterviewRecordingChunk } from "../services/apiInterviewService";
import { v7 as uuidV7 } from "uuid";

export const useAutoChunkedRecording = (
  interviewId,
  meetingJoined,
  mMeeting,
) => {
  const [recordingStatus, setRecordingStatus] = useState("idle");
  const [uploadProgress, setUploadProgress] = useState({
    uploaded: 0,
    total: 0,
  });
  const isOfflineRef = useRef(false);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const chunkCountRef = useRef(0);
  const uploadQueueRef = useRef([]);
  const isUploadingRef = useRef(false);
  const recordingStartedRef = useRef(false);
  const chunkTimeoutRef = useRef(null);
  const recordingStartTimestampRef = useRef(null);
  const lastChunkTimeRef = useRef(null);

  // NEW: Audio mixing refs
  const audioContextRef = useRef(null);
  const destinationRef = useRef(null);
  const sourceNodesRef = useRef(new Map());

  // Q&A-based recording state
  const recordingForQARef = useRef(false);
  const currentChunkStartTimeRef = useRef(null);
  const qaSessionIdRef = useRef("");

  // FIX: Snapshot of session_id taken at stopQARecording() time.
  // processAccumulatedChunks fires asynchronously via handleStop AFTER
  // stopQARecording clears recordingForQARef, so qaSessionIdRef may already
  // be "" by then. This ref bridges that gap.
  const pendingFlushSessionIdRef = useRef("");

  // FIX: Track whether a reconnection is in progress so processUploadQueue
  // does not wipe session_id/timestamps while we are mid-reconnect.
  const isReconnectingRef = useRef(false);

  const generateQASessionId = () => {
    try {
      return uuidV7();
    } catch (error) {
      // Fallback to built-in randomUUID for environments that do not support v7,
      // or fallback to a simple UUIDv4-like generator.
      if (
        typeof window !== "undefined" &&
        window.crypto &&
        window.crypto.randomUUID
      ) {
        return window.crypto.randomUUID();
      }
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }
  };

  const getCurrentQASessionId = () => qaSessionIdRef.current || "";

  const setCurrentQASessionId = (id) => {
    qaSessionIdRef.current = id || "";
    if (typeof window !== "undefined") {
      window.__qaSessionId = id || "";
      // __lastKnownSessionId is intentionally never cleared — it holds the most
      // recent real session_id so that transcripts arriving between Q&A turns
      // (after the queue drain wiped __qaSessionId) still have a valid ID to use.
      if (id) {
        window.__lastKnownSessionId = id;
      }
    }
  };

  // Stop recording
  const stopRecording = useCallback(() => {
    if (!recordingStartedRef.current) {
      return;
    }
    // Keep timestamps and session_id until uploads complete, cleanup in processUploadQueue
    recordingStartedRef.current = false;
    recordingForQARef.current = false;

    if (chunkTimeoutRef.current) {
      clearTimeout(chunkTimeoutRef.current);
      chunkTimeoutRef.current = null;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }

    // Cleanup audio context and nodes
    if (sourceNodesRef.current) {
      sourceNodesRef.current.forEach((node) => {
        try {
          node.disconnect();
        } catch (e) {}
      });
      sourceNodesRef.current.clear();
    }

    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        audioContextRef.current.close();
      } catch (e) {}
      audioContextRef.current = null;
    }

    destinationRef.current = null;

    // Always cleanup refs
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch {}
      mediaRecorderRef.current = null;
    }
  }, []);

  // Listen for global stop event
  useEffect(() => {
    const handleForceStop = () => {
      stopRecording();
    };

    window.addEventListener("force-stop-recording", handleForceStop);
    return () => {
      window.removeEventListener("force-stop-recording", handleForceStop);
      stopRecording();
    };
  }, [stopRecording]);

  // Listen for FBK received event - stop recording and prevent further uploads
  useEffect(() => {
    const handleFBKReceived = () => {
      console.warn(
        "FBK received - stopping video recording and clearing upload queue",
      );
      stopRecording();
      // Clear any pending uploads to prevent API calls
      uploadQueueRef.current = [];
      isUploadingRef.current = false;
      setUploadProgress({ uploaded: 0, total: 0 });
    };

    window.addEventListener("fbk-received", handleFBKReceived);
    return () => {
      window.removeEventListener("fbk-received", handleFBKReceived);
    };
  }, [stopRecording]);

  // FIX: Listen for reconnect start/end events dispatched by Interview.js
  // so we know not to clear session_id while a reconnection is in flight.
  useEffect(() => {
    const handleReconnectStart = () => {
      console.log("[Recording] Reconnect started — preserving session_id");
      isReconnectingRef.current = true;
    };
    const handleReconnectEnd = () => {
      console.log("[Recording] Reconnect ended — session_id guard released");
      isReconnectingRef.current = false;
    };

    window.addEventListener("interview-reconnect-start", handleReconnectStart);
    window.addEventListener("interview-reconnect-end", handleReconnectEnd);

    return () => {
      window.removeEventListener(
        "interview-reconnect-start",
        handleReconnectStart,
      );
      window.removeEventListener("interview-reconnect-end", handleReconnectEnd);
    };
  }, []);

  // Get supported MIME type
  const getSupportedMimeType = () => {
    const types = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    return (
      types.find((type) => MediaRecorder.isTypeSupported(type)) || "video/webm"
    );
  };

  // Validate blob data before upload
  const validateBlob = async (blob, chunkIndex) => {
    if (blob.size === 0) {
      console.error(`Chunk ${chunkIndex} is empty!`);
      return false;
    }

    if (blob.size < 1024) {
      console.warn(`Chunk ${chunkIndex} is very small (${blob.size} bytes)`);
    }

    try {
      const arrayBuffer = await blob.slice(0, 16).arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const allZero = uint8Array.every((byte) => byte === 0);
      if (allZero) {
        console.error(`Chunk ${chunkIndex} contains only null bytes!`);
        return false;
      }
    } catch (error) {
      console.error(
        ` Failed to read blob header for chunk ${chunkIndex}:`,
        error,
      );
      return false;
    }

    return true;
  };

  // Upload chunk to server — delegates to apiInterviewService which owns the
  // shared token-refresh cycle (isRefreshing / refreshSubscribers). Concurrent
  // chunk uploads that all 401 will queue behind a single refresh call.
  //
  // FIX: snapshotSessionId, snapshotRecordingStartTs, snapshotSpeechStartTs are
  // captured at enqueue time (in processAccumulatedChunks) so that async upload
  // delays or queue-drain clearing of __qaSessionId never produce a stale/empty
  // session_id here.
  //
  // FIX (Bug 3): Wrapped in useCallback so processUploadQueue always holds a
  // stable reference. Previously uploadChunk was a plain async function re-created
  // on every render; processUploadQueue's empty dep array meant it closed over the
  // first-render version, causing stale interviewId / stopRecording captures.
  const uploadChunk = useCallback(
    async (
      blob,
      chunkIndex,
      audioBandwidth,
      videoBandwidth,
      snapshotSessionId,
      snapshotRecordingStartTs,
      snapshotSpeechStartTs,
    ) => {
      if (!interviewId) {
        throw new Error("Interview ID is required");
      }

      const isValid = await validateBlob(blob, chunkIndex);
      if (!isValid) {
        throw new Error(`Invalid blob data for chunk ${chunkIndex}`);
      }

      const mimeType = getSupportedMimeType();

      // Use snapshotted values captured at enqueue time.
      // Fall back to globals only as a last resort (e.g. direct calls outside queue).
      const recordingStartTs =
        snapshotRecordingStartTs ||
        window.__recordingStartTimestamp ||
        recordingStartTimestampRef.current;
      const speechStartTs =
        snapshotSpeechStartTs ||
        window.__speechStartTimestamp ||
        recordingStartTs ||
        new Date().toISOString();

      try {
        const result = await uploadInterviewRecordingChunk({
          blob,
          chunkIndex,
          interviewId,
          // Use snapshotted session ID; fall back to current only if snapshot missing.
          sessionId: snapshotSessionId ?? getCurrentQASessionId(),
          recordingStartTimestamp: recordingStartTs,
          speechStartTimestamp: speechStartTs,
          mimeType,
          audioBandwidth,
          videoBandwidth,
        });

        setUploadProgress((prev) => ({
          uploaded: prev.uploaded + 1,
          total: prev.total,
        }));

        return result;
      } catch (error) {
        console.error(`Failed to upload chunk ${chunkIndex}:`, error);

        // Session expired after exhausting refresh — stop recording so the user
        // is not silently losing data with no feedback.
        if (error.message.includes("Session expired")) {
          console.error("Session expired during upload — stopping recording");
          setRecordingStatus("error");
          stopRecording();
        }

        throw error;
      }
    },
    [interviewId, stopRecording],
  );

  // Process upload queue
  const processUploadQueue = useCallback(async () => {
    // If already uploading, do not start another cycle
    if (isOfflineRef.current) {
      console.warn("⏸ Upload paused — offline");
      return;
    }

    if (isUploadingRef.current) return;

    // Nothing to upload
    if (uploadQueueRef.current.length === 0) return;

    isUploadingRef.current = true;
    setRecordingStatus(
      recordingStartedRef.current ? "recording-uploading" : "uploading",
    );

    const MAX_CHUNK_ATTEMPTS = 3;
    const RETRY_BASE_DELAY_MS = 500; // 500ms → 1s → 2s (exponential)

    while (uploadQueueRef.current.length > 0) {
      // Always peek at the HEAD — only shift() on success or exhaustion.
      const {
        blob,
        chunkIndex,
        audioBandwidth,
        videoBandwidth,
        sessionId,
        recordingStartTimestamp,
        speechStartTimestamp,
      } = uploadQueueRef.current[0];

      // ── Last-resort session_id guard ────────────────────────────────────────
      if (!sessionId) {
        console.error(
          `[Recording] processUploadQueue: chunk ${chunkIndex} has empty session_id — ` +
            "skipping. This should never happen; check processAccumulatedChunks guard.",
        );
        uploadQueueRef.current.shift(); // remove bad item so queue can drain
        setRecordingStatus("error");
        continue;
      }
      // ────────────────────────────────────────────────────────────────────────

      let lastError = null;
      let uploaded = false;

      for (let attempt = 1; attempt <= MAX_CHUNK_ATTEMPTS; attempt++) {
        try {
          await uploadChunk(
            blob,
            chunkIndex,
            audioBandwidth,
            videoBandwidth,
            sessionId,
            recordingStartTimestamp,
            speechStartTimestamp,
          );
          uploaded = true;
          break; // success — exit retry loop
        } catch (error) {
          lastError = error;

          // Auth session expired — no point retrying any chunk; stop everything.
          if (error.message.includes("Session expired")) {
            console.error(
              `[Recording] Session expired on chunk ${chunkIndex} attempt ${attempt} — halting queue.`,
            );
            setRecordingStatus("error");
            isUploadingRef.current = false;
            return; // exit processUploadQueue entirely
          }

          if (attempt < MAX_CHUNK_ATTEMPTS) {
            const delayMs = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
            console.warn(
              `[Recording] Chunk ${chunkIndex} attempt ${attempt}/${MAX_CHUNK_ATTEMPTS} failed — ` +
                `retrying in ${delayMs}ms…`,
              error.message,
            );
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
      }

      // Remove chunk from queue regardless of outcome — success or exhausted.
      uploadQueueRef.current.shift();

      if (uploaded) {
        setUploadProgress((prev) => ({
          uploaded: prev.uploaded + 1,
          total: prev.total,
        }));
        console.log(`[Recording] Chunk ${chunkIndex} uploaded successfully.`);
      } else {
        // All attempts failed — log and move on to N+1.
        console.error(
          `[Recording] Chunk ${chunkIndex} FAILED after ${MAX_CHUNK_ATTEMPTS} attempts — ` +
            "skipping to next chunk.",
          lastError?.message,
        );
        setRecordingStatus("error");
        // Do NOT halt — continue the while loop for the next chunk.
      }
    }

    isUploadingRef.current = false;

    // If still pending uploads, continue automatically
    if (uploadQueueRef.current.length > 0) {
      processUploadQueue();
    } else {
      // No pending uploads
      if (recordingStartedRef.current) {
        setRecordingStatus("recording");
      } else {
        setRecordingStatus("idle");
      }

      // FIX: Only clear session_id and timestamps when recording is truly done,
      // the queue is fully empty, AND we are not in the middle of a reconnection.
      // Previously this would wipe session_id as soon as the upload queue drained
      // after a disconnect, causing the next Q&A chunk (post-reconnect) to be sent
      // with session_id="".
      if (
        !recordingStartedRef.current &&
        !isReconnectingRef.current &&
        uploadQueueRef.current.length === 0
      ) {
        console.log(
          "[Recording] Queue drained and not reconnecting — clearing session state",
        );
        recordingStartTimestampRef.current = null;
        window.__recordingStartTimestamp = null;
        window.__speechStartTimestamp = null;
        setCurrentQASessionId("");
      } else if (!recordingStartedRef.current && isReconnectingRef.current) {
        console.log(
          "[Recording] Queue drained but reconnect in progress — preserving session_id:",
          qaSessionIdRef.current,
        );
      }
    }
    // FIX (Bug 3): uploadChunk is now a stable useCallback ref, so it is safe to
    // include here. This ensures processUploadQueue never closes over a stale
    // uploadChunk that captured an outdated interviewId or stopRecording.
  }, [uploadChunk]);

  useEffect(() => {
    const handleOffline = () => {
      console.warn("Network offline — pausing uploads");
      isOfflineRef.current = true;
    };

    const handleOnline = () => {
      isOfflineRef.current = false;

      // Resume upload safely
      processUploadQueue();
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [processUploadQueue]);

  // Handle data available - collect chunks
  const handleDataAvailable = useCallback((event) => {
    if (event.data && event.data.size > 0) {
      chunksRef.current.push(event.data);
    }
  }, []);

  // Create and upload blob from accumulated chunks
  const processAccumulatedChunks = useCallback(async () => {
    if (chunksRef.current.length === 0) {
      console.warn("No chunks to process");
      return;
    }

    const mimeType = getSupportedMimeType();
    const blob = new Blob(chunksRef.current, { type: mimeType });
    const chunkIndex = chunkCountRef.current++;

    const sizeBytes = blob.size;

    const now = Date.now();

    let durationSec = 1;
    if (lastChunkTimeRef.current) {
      durationSec = (now - lastChunkTimeRef.current) / 1000;
    }

    lastChunkTimeRef.current = now;

    const totalKbps = (sizeBytes * 8) / 1000 / durationSec;

    const videoRatio = 250 / (250 + 32);
    const audioRatio = 32 / (250 + 32);

    const videoBandwidthKbps = Number((totalKbps * videoRatio).toFixed(2));
    const audioBandwidthKbps = Number((totalKbps * audioRatio).toFixed(2));

    // FIX: Use pendingFlushSessionIdRef first — it was snapshotted synchronously
    // inside stopQARecording() before any flags were cleared, guaranteeing it
    // holds the correct ID even when this callback fires asynchronously after
    // the queue drain has already wiped qaSessionIdRef.
    // Fall back to getCurrentQASessionId() for chunks produced mid-recording
    // (not from a stop flush), then clear the pending ref so it doesn't leak
    // into the next Q&A session.
    const snapshotSessionId =
      pendingFlushSessionIdRef.current || getCurrentQASessionId();
    pendingFlushSessionIdRef.current = ""; // consumed

    const snapshotRecordingStartTs =
      window.__recordingStartTimestamp || recordingStartTimestampRef.current;
    const snapshotSpeechStartTs =
      window.__speechStartTimestamp ||
      snapshotRecordingStartTs ||
      new Date().toISOString();

    // ── Inner helper: push to queue once we have a valid session_id ──────────
    const _enqueueChunk = (resolvedSessionId) => {
      console.log(
        `[Recording] Enqueuing chunk ${chunkIndex} with session_id="${resolvedSessionId}"`,
      );
      try {
        uploadQueueRef.current.push({
          blob,
          chunkIndex,
          audioBandwidth: audioBandwidthKbps,
          videoBandwidth: videoBandwidthKbps,
          // Store snapshots so processUploadQueue always passes the correct
          // values to uploadChunk — never stale globals.
          sessionId: resolvedSessionId,
          recordingStartTimestamp: snapshotRecordingStartTs,
          speechStartTimestamp: snapshotSpeechStartTs,
        });
        setUploadProgress((prev) => ({ ...prev, total: prev.total + 1 }));
        chunksRef.current = [];
        processUploadQueue();
      } catch (error) {
        console.error(`Failed to enqueue chunk ${chunkIndex}:`, error);
        setRecordingStatus("error");
      }
    };
    // ────────────────────────────────────────────────────────────────────────

    // ── session_id guard ─────────────────────────────────────────────────────
    // Never enqueue a chunk with an empty session_id — it corrupts server-side
    // grouping. If session_id is still empty (race: this callback fired before
    // startQARecording wrote the ID), poll for up to 3 s then drop the chunk.
    if (!snapshotSessionId) {
      console.warn(
        `[Recording] Chunk ${chunkIndex}: session_id empty at enqueue time — ` +
          "polling up to 3s before giving up…",
      );

      const SESSION_POLL_MS = 100;
      const SESSION_POLL_MAX = 3000;
      let waited = 0;

      const pollForSessionId = () => {
        const liveId =
          getCurrentQASessionId() ||
          (typeof window !== "undefined" && window.__lastKnownSessionId) ||
          "";

        if (liveId) {
          console.log(
            `[Recording] session_id resolved to "${liveId}" after ${waited}ms — enqueuing chunk ${chunkIndex}`,
          );
          _enqueueChunk(liveId);
          return;
        }

        waited += SESSION_POLL_MS;
        if (waited >= SESSION_POLL_MAX) {
          // Final fallback — use __lastKnownSessionId rather than dropping the chunk.
          const lastKnown =
            (typeof window !== "undefined" && window.__lastKnownSessionId) ||
            "";
          if (lastKnown) {
            console.warn(
              `[Recording] Chunk ${chunkIndex}: using last known session_id "${lastKnown}" ` +
                `after ${SESSION_POLL_MAX}ms wait.`,
            );
            _enqueueChunk(lastKnown);
          } else {
            console.error(
              `[Recording] Chunk ${chunkIndex} DROPPED — no session_id available after ` +
                `${SESSION_POLL_MAX}ms. Upload skipped to avoid corrupting server-side grouping.`,
            );
            setRecordingStatus("error");
          }
          return;
        }

        setTimeout(pollForSessionId, SESSION_POLL_MS);
      };

      setTimeout(pollForSessionId, SESSION_POLL_MS);
      return; // will resume inside pollForSessionId
    }
    // ────────────────────────────────────────────────────────────────────────

    _enqueueChunk(snapshotSessionId);
  }, [processUploadQueue]);

  // Handle MediaRecorder stop event
  const handleStop = useCallback(() => {
    // FIX (Bug 2): Guard against a stale onstop firing from a previous MediaRecorder
    // instance after a fast stop/start cycle. If chunksRef is already empty and
    // recording is no longer active, this is a ghost event — skip it to avoid
    // a silent no-op log or (worse) pushing an empty chunk to the queue.
    if (
      chunksRef.current.length === 0 &&
      !recordingStartedRef.current &&
      !recordingForQARef.current
    ) {
      console.warn(
        "[Recording] handleStop: no chunks and not recording — skipping stale onstop",
      );
      return;
    }
    processAccumulatedChunks();
  }, [processAccumulatedChunks]);

  // NEW: Create mixed audio stream with all participants
  const createMixedAudioStream = useCallback(async () => {
    try {
      // Create or reuse AudioContext with proper sample rate
      if (
        !audioContextRef.current ||
        audioContextRef.current.state === "closed"
      ) {
        audioContextRef.current = new (
          window.AudioContext || window.webkitAudioContext
        )({
          sampleRate: 48000, // Standard sample rate for WebRTC
          latencyHint: "interactive",
        });
      }

      const audioContext = audioContextRef.current;

      // Resume if suspended
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      // Create destination for mixed audio
      const destination = audioContext.createMediaStreamDestination();
      destinationRef.current = destination;

      // Clear old source nodes
      sourceNodesRef.current.forEach((node) => {
        try {
          node.disconnect();
        } catch (e) {}
      });
      sourceNodesRef.current.clear();

      let audioSourcesAdded = 0;

      // Add local participant's microphone audio
      if (mMeeting?.localParticipant?.micStream) {
        try {
          const localMicStream = mMeeting.localParticipant.micStream;
          const mediaStream = new MediaStream([localMicStream.track]);

          // Check if track is live
          if (localMicStream.track) {
            const localSource =
              audioContext.createMediaStreamSource(mediaStream);

            // Add gain node for volume control
            const gainNode = audioContext.createGain();
            gainNode.gain.value = 1.0;

            localSource.connect(gainNode);
            gainNode.connect(destination);

            sourceNodesRef.current.set("local", localSource);
            audioSourcesAdded++;
          }
        } catch (error) {
          console.warn("Could not add local audio:", error);
        }
      }

      // Add all remote participants' audio
      const participants = Array.from(mMeeting.participants.values());

      for (const participant of participants) {
        try {
          if (participant.micStream && participant.micStream.track) {
            const mediaStream = new MediaStream([participant.micStream.track]);

            // Check if track is live
            if (participant.micStream.track.readyState === "live") {
              const remoteSource =
                audioContext.createMediaStreamSource(mediaStream);

              // Add gain node for volume control
              const gainNode = audioContext.createGain();
              gainNode.gain.value = 1.0;

              remoteSource.connect(gainNode);
              gainNode.connect(destination);

              sourceNodesRef.current.set(participant.id, remoteSource);
              audioSourcesAdded++;
            }
          }
        } catch (error) {
          console.warn(
            ` Could not add audio for participant ${participant.id}:`,
            error,
          );
        }
      }

      // Ensure we have at least one audio source
      if (audioSourcesAdded === 0) {
        console.warn(" No audio sources were added to the mix");
        return null;
      }
      try {
        const botAudioEl = document.getElementById("botAudio");
        if (botAudioEl) {
          const botSource = audioContext.createMediaElementSource(botAudioEl);
          const botGain = audioContext.createGain();
          botGain.gain.value = 1.0; // Adjust if too loud or low
          botSource.connect(botGain);
          botGain.connect(destination);
          sourceNodesRef.current.set("botPlayback", botSource);
        } else {
          console.warn("botAudio element not found in DOM");
        }
      } catch (error) {
        console.error("Failed to add bot/panel playback audio:", error);
      }
      return destination.stream;
    } catch (error) {
      console.error("Failed to create mixed audio stream:", error);
      return null;
    }
  }, [mMeeting]);

  // Get combined video and mixed audio stream
  const getCombinedMediaStream = useCallback(async () => {
    if (!mMeeting?.localParticipant) {
      console.warn("No local participant available");
      return null;
    }

    try {
      const tracks = [];

      // Get local video if available
      if (mMeeting.localParticipant.webcamStream) {
        const videoTrack =
          mMeeting.localParticipant.webcamStream.getVideoTracks()[0];
        if (videoTrack && videoTrack.readyState === "live") {
          try {
            await videoTrack.applyConstraints({
              width: 640,
              height: 480,
              frameRate: 15,
            });
          } catch (e) {
            console.warn("Could not apply constraints:", e);
          }

          tracks.push(videoTrack);
        }
      }

      // Create mixed audio stream with all participants
      const mixedAudioStream = await createMixedAudioStream();
      if (mixedAudioStream) {
        const audioTracks = mixedAudioStream.getAudioTracks();
        if (audioTracks.length > 0 && audioTracks[0].readyState === "live") {
          tracks.push(audioTracks[0]);
        } else {
          console.warn("Mixed audio track is not live");
        }
      }

      // Must have at least one track (prefer audio)
      if (tracks.length === 0) {
        console.warn("No SDK tracks — trying fallback getUserMedia");

        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true,
          });
          return fallbackStream;
        } catch (e) {
          console.error("Fallback failed:", e);
          throw new Error("No valid media tracks available");
        }
      }

      const combinedStream = new MediaStream(tracks);

      // Verify stream is active
      if (!combinedStream.active) {
        throw new Error("Combined stream is not active");
      }

      return combinedStream;
    } catch (error) {
      console.error("Failed to get combined media stream:", error);

      // Fallback: try getUserMedia directly
      console.warn("Falling back to getUserMedia");
      try {
        const userStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 48000,
          },
        });

        return userStream;
      } catch (fallbackError) {
        console.error("Fallback getUserMedia failed:", fallbackError);

        // Last resort: audio only
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              sampleRate: 48000,
            },
            video: false,
          });

          return audioStream;
        } catch (audioError) {
          console.error(" All fallback attempts failed:", audioError);
          return null;
        }
      }
    }
  }, [mMeeting, createMixedAudioStream]);

  // Start recording function
  const startRecording = useCallback(async () => {
    if (
      !meetingJoined ||
      !interviewId ||
      recordingStartedRef.current ||
      window.__FBK_RECEIVED__
    ) {
      console.warn("Cannot start recording: conditions not met", {
        meetingJoined,
        interviewId,
        alreadyRecording: recordingStartedRef.current,
        fbkReceived: window.__FBK_RECEIVED__,
      });
      return false;
    }

    try {
      lastChunkTimeRef.current = Date.now();
      // Get the combined media stream (video + mixed audio)
      const combinedStream = await getCombinedMediaStream();

      if (!combinedStream) {
        throw new Error("Unable to obtain media stream");
      }

      // Verify stream has active tracks
      const videoTracks = combinedStream.getVideoTracks();
      const audioTracks = combinedStream.getAudioTracks();

      if (audioTracks.length === 0 && videoTracks.length === 0) {
        throw new Error("No active tracks in media stream");
      }

      streamRef.current = combinedStream;
      const now = new Date().toISOString();

      // recording start (refresh each QA start)
      recordingStartTimestampRef.current = now;
      window.__recordingStartTimestamp = now;

      // do not set speech timestamp here; it should come from user action (space/mic) to keep separation

      // Create MediaRecorder with supported options
      const mimeType = getSupportedMimeType();

      let options = {
        mimeType: mimeType,
      };

      // Set bitrates based on available tracks
      if (videoTracks.length > 0) {
        options.videoBitsPerSecond = 250000; // 250 kbps
      }

      if (audioTracks.length > 0) {
        options.audioBitsPerSecond = 32000; // 32 kbps
      }
      try {
        mediaRecorderRef.current = new MediaRecorder(combinedStream, options);
      } catch (e) {
        console.warn(
          "Could not create MediaRecorder with options, trying without bitrate:",
          e,
        );
        // Retry without bitrate options
        mediaRecorderRef.current = new MediaRecorder(combinedStream, {
          mimeType,
        });
      }

      // Set up event handlers
      mediaRecorderRef.current.ondataavailable = handleDataAvailable;
      mediaRecorderRef.current.onstop = handleStop;
      mediaRecorderRef.current.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setRecordingStatus("error");
      };

      mediaRecorderRef.current.onstart = () => {};

      // Handle stream ending
      combinedStream.getTracks().forEach((track) => {
        track.onended = () => {
          console.warn(`Track ended: ${track.kind} - ${track.label}`);
        };
      });

      // Initialize state
      recordingStartedRef.current = true;
      setRecordingStatus("recording");
      chunkCountRef.current = 0;
      chunksRef.current = [];
      setUploadProgress({ uploaded: 0, total: 0 });

      // Start recording with 1 second timeslice for better chunking
      mediaRecorderRef.current.start(1000);

      return true;
    } catch (error) {
      console.error(" Failed to start recording:", error);
      setRecordingStatus("error");
      recordingStartedRef.current = false;

      // Cleanup on error
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      return false;
    }
  }, [
    meetingJoined,
    interviewId,
    mMeeting,
    handleDataAvailable,
    handleStop,
    getCombinedMediaStream,
  ]);

  // Start recording for current Q&A
  const startQARecording = useCallback(async () => {
    if (recordingStartedRef.current || window.__FBK_RECEIVED__) {
      console.warn(" Recording already in progress or FBK received");
      return;
    }

    // FIX (Bug 1): Browsers auto-suspend AudioContext after ~30 seconds of no
    // user interaction. Resume it here — triggered by a user gesture (posting an
    // answer) — before we try to build the mixed audio stream.  Without this,
    // createMixedAudioStream can return null after a period of silence, causing
    // getCombinedMediaStream to fall through to a slow getUserMedia fallback or
    // throw, and the whole recording start is skipped.
    if (
      audioContextRef.current &&
      audioContextRef.current.state === "suspended"
    ) {
      try {
        await audioContextRef.current.resume();
        console.log("[Recording] AudioContext resumed before QA start");
      } catch (e) {
        console.warn("[Recording] AudioContext resume failed:", e);
      }
    }

    // FIX: Generate and set the new session ID FIRST before clearing the old one,
    // so there is never a window where session_id is "" between the two operations.
    // Previously: setCurrentQASessionId("") → generateQASessionId() → setCurrentQASessionId(newId)
    // meant any in-flight read during the gap got an empty string.
    const newSessionId = generateQASessionId();
    setCurrentQASessionId(newSessionId);
    console.log("[Recording] New QA session started:", newSessionId);

    recordingForQARef.current = true;
    currentChunkStartTimeRef.current = Date.now();

    startRecording();
  }, [startRecording]);

  // Stop and upload current Q&A recording
  const stopQARecording = useCallback(() => {
    if (!recordingStartedRef.current || !recordingForQARef.current) {
      console.warn(" No active Q&A recording to stop");
      return;
    }

    // FIX: Snapshot session_id NOW, before clearing flags or calling stop().
    // handleStop → processAccumulatedChunks fires after this function returns,
    // by which point recordingForQARef is false and the async queue-drain may
    // have already wiped qaSessionIdRef. The pending ref carries the ID safely
    // across that gap.
    pendingFlushSessionIdRef.current = getCurrentQASessionId();
    console.log(
      "[Recording] stopQARecording — snapshotting session_id for flush:",
      pendingFlushSessionIdRef.current,
    );

    recordingForQARef.current = false;
    recordingStartedRef.current = false;

    if (chunkTimeoutRef.current) {
      clearTimeout(chunkTimeoutRef.current);
      chunkTimeoutRef.current = null;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      // FIX (Bug 4): Force-flush any buffered data before stopping.
      // Without this, answers shorter than the 1-second timeslice produce zero
      // chunks and the whole recording is silently dropped in processAccumulatedChunks.
      try {
        mediaRecorderRef.current.requestData();
      } catch (e) {
        console.warn("[Recording] requestData() failed before stop:", e);
      }
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }

    // Cleanup audio context
    if (sourceNodesRef.current) {
      sourceNodesRef.current.forEach((node) => {
        try {
          node.disconnect();
        } catch (e) {}
      });
      sourceNodesRef.current.clear();
    }

    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        audioContextRef.current.close();
      } catch (e) {}
      audioContextRef.current = null;
    }

    // Cleanup mediaRecorder
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch {}
      mediaRecorderRef.current = null;
    }
  }, []);

  // Listen for bot-started-speaking event to start Q&A recording.
  // Debounce rapid duplicate fires within 300 ms — recordingStartedRef
  // already prevents a second session from starting while one is active.
  useEffect(() => {
    let debounceTimer = null;

    const handleBotStartedSpeaking = () => {
      if (window.__FBK_RECEIVED__) {
        console.warn("FBK received - not starting Q&A recording");
        return;
      }

      // Debounce: ignore duplicate events within 300 ms
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
      }, 300);

      startQARecording();
    };

    window.addEventListener("bot-started-speaking", handleBotStartedSpeaking);

    return () => {
      window.removeEventListener(
        "bot-started-speaking",
        handleBotStartedSpeaking,
      );
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [startQARecording]);

  // Auto-stop recording when meeting ends
  useEffect(() => {
    if (!meetingJoined && recordingStartedRef.current) {
      stopRecording();
    }
  }, [meetingJoined, stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  // Status helpers
  const getStatusText = () => {
    switch (recordingStatus) {
      case "recording":
        return "Recording Call...";
      case "recording-uploading":
        return "Recording & Uploading...";
      case "uploading":
        return "Finalizing Upload...";
      case "error":
        return "Recording Error";
      default:
        return "Ready";
    }
  };

  const isActivelyRecording = recordingStartedRef.current;

  return {
    isRecording: isActivelyRecording,
    recordingStatus,
    uploadProgress,
    getStatusText,
    pendingUploads: uploadQueueRef.current.length,
    startRecording,
    stopRecording,
    stopQARecording,
    startQARecording,
  };
};
